import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GitBranch, Clock, CheckCircle, XCircle, Send, Search, Trash2, Download, ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-50 text-blue-600 border-blue-200', icon: Send },
  accepted: { label: 'Accepted', color: 'bg-emerald-50 text-emerald-600 border-emerald-200', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-50 text-red-600 border-red-200', icon: XCircle },
};

export default function ChangeOrdersList() {
  const navigate = useNavigate();
  const [cos, setCos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { fetchCos(); }, []);

  const fetchCos = async () => {
    try {
      const res = await fetch(`${API}/change-orders`, { credentials: 'include' });
      if (res.ok) setCos(await res.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await fetch(`${API}/change-orders/${deleteId}`, { method: 'DELETE', credentials: 'include' });
      setCos(prev => prev.filter(c => c.change_order_id !== deleteId));
      toast.success('Change order deleted');
    } catch { toast.error('Failed to delete'); }
    setDeleteId(null);
  };

  // Inline status change — optimistic, rolls back on failure.
  const handleStatusChange = async (id, newStatus) => {
    const prev = cos;
    setCos(p => p.map(c => c.change_order_id === id ? { ...c, status: newStatus } : c));
    try {
      const res = await fetch(`${API}/change-orders/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Marked as ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch {
      setCos(prev);
      toast.error('Failed to update status');
    }
  };

  const handleExportPdf = async (id, coNum, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/change-orders/${id}/pdf`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${coNum}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      }
    } catch { toast.error('Failed to export PDF'); }
  };

  const calcAdjustment = (co) => {
    let sub = 0;
    (co.sections || []).forEach(s => {
      (s.items || []).forEach(item => {
        sub += (item.qty || 0) * (item.unit_price || 0);
      });
    });
    const gst = sub * (co.tax_gst || 5) / 100;
    const qst = sub * (co.tax_qst || 9.975) / 100;
    return sub + gst + qst;
  };

  const filtered = cos.filter(co => {
    const matchSearch = !search
      || co.title?.toLowerCase().includes(search.toLowerCase())
      || co.change_order_number?.toLowerCase().includes(search.toLowerCase())
      || co.parent_estimate_number?.toLowerCase().includes(search.toLowerCase())
      || co.client?.name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || co.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="animate-fade-in" data-testid="change-orders-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Change Orders
          </h1>
          <p className="text-sm text-[#6B7A73] mt-1">
            {cos.length} total · Revisions to existing estimates
          </p>
        </div>
        <Button
          data-testid="new-co-from-list-btn"
          onClick={() => navigate('/estimates')}
          variant="outline"
          className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-5 py-2.5 font-medium"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Create from Estimate
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7A73]" />
          <Input
            data-testid="search-cos-input"
            placeholder="Search change orders..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-white border-[#E6EBE8] text-[#1A1D1C] rounded-md focus:border-[#2D6B68] focus:ring-1 focus:ring-[#2D6B68]"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white border-[#E6EBE8] rounded-md" data-testid="co-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E6EBE8] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <GitBranch className="w-12 h-12 text-[#E6EBE8] mx-auto mb-4" />
            <p className="text-[#6B7A73] text-sm">
              {search || statusFilter !== 'all'
                ? 'No change orders match your filters.'
                : 'No change orders yet. Open an estimate and click "New Change Order" to create one.'}
            </p>
          </div>
        ) : (
          <table className="w-full" data-testid="cos-table">
            <thead>
              <tr className="bg-[#F9F9F8] border-b border-[#E6EBE8]">
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-left font-semibold">Client</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-left font-semibold">Change Order</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-left font-semibold">Parent Estimate</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-left font-semibold hidden lg:table-cell">Date</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-right font-semibold">Adjustment</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-center font-semibold">Status</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6EBE8]">
              {filtered.map(co => {
                const cfg = STATUS_CONFIG[co.status] || STATUS_CONFIG.draft;
                const StatusIcon = cfg.icon;
                const adjustment = calcAdjustment(co);
                const isCredit = adjustment < 0;
                return (
                  <tr
                    key={co.change_order_id}
                    className="hover:bg-[#F9F9F8] transition-colors cursor-pointer"
                    onClick={() => navigate(`/change-orders/${co.change_order_id}`)}
                    data-testid={`co-row-${co.change_order_id}`}
                  >
                    <td className="py-3 px-4">
                      <p className="text-sm font-bold text-[#1A1D1C]">
                        {co.client?.company || co.client?.name || '—'}
                      </p>
                      {co.client?.company && co.client?.name && (
                        <p className="text-[10px] text-[#6B7A73] mt-0.5">{co.client.name}</p>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm font-medium text-[#1A1D1C]">{co.title || '—'}</p>
                      <p className="text-xs text-[#6B7A73]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {co.change_order_number}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/estimates/${co.parent_estimate_id}`); }}
                        className="text-xs text-[#2D6B68] hover:underline"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                      >
                        {co.parent_estimate_number}
                      </button>
                    </td>
                    <td className="py-3 px-4 hidden lg:table-cell">
                      <p className="text-sm text-[#6B7A73]">
                        {co.created_at ? new Date(co.created_at).toLocaleDateString() : '—'}
                      </p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span
                        className={`text-sm font-medium ${isCredit ? 'text-red-600' : 'text-[#1A1D1C]'}`}
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                      >
                        {isCredit ? '−' : ''}${Math.abs(adjustment).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={co.status || 'draft'}
                        onValueChange={(v) => handleStatusChange(co.change_order_id, v)}
                      >
                        <SelectTrigger
                          className={`${cfg.color} border rounded-full px-2.5 py-0.5 h-auto text-xs font-medium inline-flex items-center gap-1 w-auto min-w-0 hover:brightness-95 transition-all [&>svg]:opacity-50 [&>svg]:ml-1`}
                          data-testid={`co-status-trigger-${co.change_order_id}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          <span>{cfg.label}</span>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CONFIG).map(([key, c]) => {
                            const Icon = c.icon;
                            return (
                              <SelectItem key={key} value={key} data-testid={`co-status-option-${key}-${co.change_order_id}`}>
                                <span className="inline-flex items-center gap-2">
                                  <Icon className="w-3 h-3" />
                                  {c.label}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => handleExportPdf(co.change_order_id, co.change_order_number, e)}
                          className="p-1.5 rounded-md text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1] transition-colors"
                          title="Export PDF"
                          data-testid={`co-pdf-btn-${co.change_order_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteId(co.change_order_id); }}
                          className="p-1.5 rounded-md text-[#6B7A73] hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                          data-testid={`co-delete-btn-${co.change_order_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Delete Change Order?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7A73]">This cannot be undone. The change order will be permanently removed.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#E6EBE8]" data-testid="co-cancel-delete-btn">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="co-confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
